/**
 * LFDL Rich Text Animé — runtime
 * Objectif: réduire le poids de l'éditeur Shopify (designMode) + éviter de reparser un gros <script> par section
 * - Initialise les cascades d'icônes
 * - Gère le mode "overflow" pour l'animation typewriter (si présent)
 * - Ajoute une pause paramétrable entre les cycles de typing (si présent)
 */
(function () {
  "use strict";

  var DESIGN_MODE = !!(window.Shopify && window.Shopify.designMode);

  var STATE = new WeakMap();

  function rafThrottle(fn) {
    var scheduled = false;
    return function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        fn();
      });
    };
  }

  function safeNumber(v, fallback) {
    var n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  }

  function initCascade(root, state) {
    var lists = root.querySelectorAll(".lfdl-iconlist--cascade");
    if (!lists.length) return;

    function activate(list) {
      if (list.classList.contains("lfdl-cascade--active")) return;
      list.classList.add("lfdl-cascade--active");
    }

    if (!("IntersectionObserver" in window)) {
      lists.forEach(activate);
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          activate(entry.target);
          io.unobserve(entry.target);
        }
      });
    }, { root: null, threshold: 0.15 });

    lists.forEach(function (list) { io.observe(list); });
    state.cascadeIO = io;
  }

  function getContentWidth(typeEl) {
    if (!typeEl) return 0;

    // Priorité au ghost (c'est précisément fait pour mesurer la largeur sans animation)
    var ghost = typeEl.querySelector(".lfdl-type__ghost");
    if (ghost) {
      var gw = ghost.scrollWidth || ghost.getBoundingClientRect().width;
      if (gw) return gw;
    }

    var textEl = typeEl.querySelector(".lfdl-type__text");
    if (textEl) {
      var sw = textEl.scrollWidth || textEl.getBoundingClientRect().width;
      if (sw) return sw;
    }

    return typeEl.scrollWidth || typeEl.getBoundingClientRect().width || 0;
  }

  function getAvailableWidth(container) {
    if (!container) return 0;

    // clientWidth = width interne sans scrollbar
    var w = container.clientWidth || container.getBoundingClientRect().width || 0;
    if (!w) return 0;

    var styles = window.getComputedStyle(container);
    var padLeft = safeNumber(styles.paddingLeft, 0);
    var padRight = safeNumber(styles.paddingRight, 0);

    var available = w - padLeft - padRight;
    return available > 0 ? available : w;
  }

  function initTypeOverflow(root, state) {
    // En designMode, c'est ce qui fait ramer le plus lors des micro-changements.
    // On le coupe volontairement pour la fluidité de l'éditeur.
    if (DESIGN_MODE) return;

    var candidates = root.querySelectorAll(".lfdl-highlight .lfdl-type, .lfdl-title .lfdl-type");
    if (!candidates.length) return;

    var containers = root.querySelectorAll(".lfdl-highlight, .lfdl-title");
    if (!containers.length) return;

    function apply() {
      containers.forEach(function (container) {
        var typeEl = container.querySelector(".lfdl-type");
        if (!typeEl) return;

        var contentWidth = getContentWidth(typeEl);
        var availableWidth = getAvailableWidth(container);

        if (!contentWidth || !availableWidth) return;

        if (contentWidth > (availableWidth + 1)) {
          typeEl.classList.add("lfdl-type--overflow");
          container.classList.add("lfdl-hl--overflow");
        } else {
          typeEl.classList.remove("lfdl-type--overflow");
          container.classList.remove("lfdl-hl--overflow");
        }
      });
    }

    var applyThrottled = rafThrottle(apply);

    // Premier passage + un petit second passage (fonts/layout settling)
    apply();
    window.setTimeout(applyThrottled, 160);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(applyThrottled).catch(function () {});
    }

    // ResizeObserver (moins violent qu'un resize global)
    if ("ResizeObserver" in window) {
      var ro = new ResizeObserver(function () {
        applyThrottled();
      });
      ro.observe(root);
      state.typeRO = ro;
    } else {
      var resizeTimer;
      var onResize = function () {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(applyThrottled, 150);
      };
      window.addEventListener("resize", onResize);
      state.onResize = onResize;
    }

    // Quand un <details> s'ouvre / se ferme, la largeur utile change
    var onToggle = function (event) {
      if (!event || !event.target || event.target.tagName !== "DETAILS") return;
      window.setTimeout(applyThrottled, 60);
    };
    root.addEventListener("toggle", onToggle);
    state.onToggle = onToggle;
  }

  function initTypingPause(root, state) {
    // En designMode: c'est "safe" mais inutile, donc on évite
    if (DESIGN_MODE) return;

    var typeEls = root.querySelectorAll(".lfdl-type__text--animate[data-lfdl-type-pause]");
    if (!typeEls.length) return;

    function getPauseMs(el) {
      var isMobile = window.matchMedia("(max-width: 749px)").matches;
      var raw = isMobile ? el.dataset.lfdlTypePauseMobile : el.dataset.lfdlTypePause;
      var pause = safeNumber(raw, 0);
      if (pause <= 0) return 0;
      return pause * 1000;
    }

    typeEls.forEach(function (el) {
      var container = el.closest(".lfdl-highlight, .lfdl-title");
      if (!container) return;

      var forward = true;
      var handler = function (event) {
        if (!event || event.animationName !== "lfdl-typing") return;

        var pauseMs = getPauseMs(el);
        if (forward && pauseMs > 0) {
          container.classList.add("lfdl-typing-paused");
          window.setTimeout(function () {
            container.classList.remove("lfdl-typing-paused");
          }, pauseMs);
        }
        forward = !forward;
      };

      el.addEventListener("animationiteration", handler);
      (state.typingHandlers || (state.typingHandlers = [])).push([el, handler]);
    });
  }

  function init(root) {
    if (!root) return;

    // Anti double init
    if (STATE.has(root)) return;

    var state = {
      cascadeIO: null,
      typeRO: null,
      onResize: null,
      onToggle: null,
      typingHandlers: null
    };

    STATE.set(root, state);

    initCascade(root, state);
    initTypeOverflow(root, state);
    initTypingPause(root, state);
  }

  function destroy(root) {
    var state = STATE.get(root);
    if (!state) return;

    if (state.cascadeIO) {
      try { state.cascadeIO.disconnect(); } catch (e) {}
    }

    if (state.typeRO) {
      try { state.typeRO.disconnect(); } catch (e) {}
    }

    if (state.onResize) {
      try { window.removeEventListener("resize", state.onResize); } catch (e) {}
    }

    if (state.onToggle) {
      try { root.removeEventListener("toggle", state.onToggle); } catch (e) {}
    }

    if (state.typingHandlers && state.typingHandlers.length) {
      state.typingHandlers.forEach(function (pair) {
        try { pair[0].removeEventListener("animationiteration", pair[1]); } catch (e) {}
      });
    }

    STATE.delete(root);
  }

  window.LFDLRichTextAnime = window.LFDLRichTextAnime || {};
  window.LFDLRichTextAnime.init = init;
  window.LFDLRichTextAnime.destroy = destroy;
})();
